import { Injectable } from "@angular/core";
import { AppUI, AsThreshold, Template } from "@identy/identy-face";
import { EnumUiOption, LocalizationItem } from "../../../modules/models/types";
import { MenuOption } from "src/app/types";
import { BehaviorSubject } from "rxjs";
import { MenuConfigService } from "../../../services/menu-config.service";
import { any } from "codelyzer/util/function";

@Injectable({
  providedIn: "root",
})
export class ContextMenuHelperService {
  public menuValues: any = {};
  private menuValuesSource = new BehaviorSubject<any>({});
  menuValues$ = this.menuValuesSource.asObservable();

  // Track previous values to detect actual changes
  private previousValues: Map<string, any> = new Map();

  public allLocalizations: EnumUiOption<LocalizationItem<any>>[] = [];
  public localization: EnumUiOption<LocalizationItem<any>>;
  public timeout: number;
  public app_ui: AppUI = AppUI.STANDARD;
  public templates: Array<Template> = [];
  public enableBackgroundRemoval?: boolean = false;
  public backgroundColor?: any = "#ffffff";

  constructor(private menuConfigService: MenuConfigService) {
    this.menuValues = {
      enable_as: true,
      enable_df: false,
      enable_icao_checks: false,
      icao_strict: false,
      icao_threshold: new Map(),
      icao_threshold_ui: {},
      enable_retake: false,
      skip_device_check: false,
      allow_camera_select: false,
      enable_eye_status: true,
      assisted: false,
      download: false,
      capture_training: true,
      enableBackgroundRemoval: false,
      backgroundColor: "#ffffff",
      as_threshold: AsThreshold.BALANCED_VERY_HIGH,
      df_threshold: AsThreshold.BALANCED_VERY_HIGH,
      templates: [Template.PNG, Template.JPEG],
      app_ui: AppUI.STANDARD,
      localization: "English",
      timeout: 30,
      vs_enable_video_recording: false,
      vs_enable_video_challenge: false,
      vs_challenge_text: "Please look at the camera and smile!",
      vs_phase2_layout: "BOTTOM",
    };
    this.menuValuesSource.next(this.menuValues);
  }

  value_change(key: string, event: any) {
    // Extract the actual value for this field
    let actualValue: any;

    if (key === "templates") {
      this.menuValues[key] = Object.keys(event[key]).filter(
        (t) => event[key][t],
      );
      actualValue = this.menuValues[key];
    } else if (key === "localization") {
      const selectedLoc = this.allLocalizations.find(
        (loc) => loc.name === event.localization,
      );
      if (selectedLoc) {
        this.localization = selectedLoc;
        this.menuValues[key] = selectedLoc.option;
        actualValue = event.localization;
      }
    } else {
      this.menuValues[key] = event[key];
      actualValue = event[key];
    }

    this.menuValuesSource.next(this.menuValues);

    // ONLY emit event if value actually changed
    // This prevents unnecessary handler triggers
    if (this.hasValueChanged(key, actualValue)) {
      this.previousValues.set(key, this.deepClone(actualValue));
      this.menuConfigService.onUserValueChange(key, actualValue);
    }
  }

  /**
   * Check if value has actually changed from previous value
   */
  private hasValueChanged(key: string, newValue: any): boolean {
    const previousValue = this.previousValues.get(key);

    // If no previous value, it's the first time - consider it changed
    if (previousValue === undefined) {
      return true;
    }

    // Deep comparison for arrays and objects
    return JSON.stringify(previousValue) !== JSON.stringify(newValue);
  }

  /**
   * Deep clone a value to store as previous value
   */
  private deepClone(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    return JSON.parse(JSON.stringify(value));
  }

  /**
   * Update runtime checkbox/radio values without modifying the config template
   * This updates the actual form values that are bound to ngModel, avoiding UI re-renders
   *
   * @param field - Field name to update
   * @param value - New value (array for multi-checkbox, single value for radio/boolean)
   * @param emitEvent - Whether to emit user value change event (default: true)
   *
   * @example
   * // Update multi-checkbox field (check specific boxes)
   * updateFieldValue('icao_threshold', ['GLASSES', 'FACE_ANGLE']);
   *
   * @example
   * // Update single-radio field
   * updateFieldValue('timeout', '30');
   *
   * @example
   * // Update boolean field
   * updateFieldValue('enable_icao_checks', true);
   */
  updateFieldValue(field: string, value: any, emitEvent: boolean = true): void {
    console.log(`[updateFieldValue] Field: ${field}, Value:`, value);

    // Check if value actually changed before updating
    const hasChanged = this.hasValueChanged(field, value);
    console.log(`[updateFieldValue] Has changed: ${hasChanged}`);

    // For multi-checkbox fields (detect by value being array)
    if (Array.isArray(value)) {
      // Ensure field exists and is an object (initialize if needed)
      if (
        !this.menuValues[field] ||
        typeof this.menuValues[field] !== "object" ||
        Array.isArray(this.menuValues[field])
      ) {
        // Initialize as empty object with all possible values set to false
        this.menuValues[field] = {};
      }

      // Create a new object to trigger Angular change detection
      const newCheckboxObj = { ...this.menuValues[field] };

      // Add any new values that don't exist yet
      value.forEach((val) => {
        if (!newCheckboxObj.hasOwnProperty(val)) {
          newCheckboxObj[val] = false;
        }
      });

      // Reset all checkboxes to false
      Object.keys(newCheckboxObj).forEach((key) => {
        newCheckboxObj[key] = false;
      });

      // Set specified values to true
      value.forEach((val) => {
        newCheckboxObj[val] = true;
      });

      // Create a new menuValues object with the updated field
      this.menuValues = {
        ...this.menuValues,
        [field]: newCheckboxObj,
      };
    } else {
      // For single-value fields (radio, boolean, text, number)
      // Create a new menuValues object with the updated field
      this.menuValues = {
        ...this.menuValues,
        [field]: value,
      };
    }

    // Emit the new menuValues reference to trigger Angular change detection
    console.log(`[updateFieldValue] Emitting new menuValues:`, this.menuValues);
    console.log(
      `[updateFieldValue] Field ${field} value:`,
      this.menuValues[field],
    );
    this.menuValuesSource.next(this.menuValues);

    // Track as a value change and emit event if requested
    if (hasChanged) {
      this.previousValues.set(field, this.deepClone(value));

      // Emit user value change event so onFieldChange handlers are triggered
      if (emitEvent) {
        console.log(`[updateFieldValue] Emitting user value change event`);
        this.menuConfigService.onUserValueChange(field, value);
      }
    }
  }
}
